import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import SearchControl from "./SearchControl";
import MapClickHandler from "./map/MapClickHandler";
import type { MapLocation } from "../utils/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { MapCenterHandler } from "./map/MapCenterHandler";
import {
  MapInteractionController,
  useMapInteractionLock,
} from "./map/MapInteractionGuard";
import { reverseGeocode } from "../utils/reverseGeocode";
import "./map/Map.css";

const MAP_CENTER: [number, number] = [55.6052931, 13.0001566];
const MAP_ZOOM = 11;

type PickupMapProps = {
  locations: MapLocation[];
  onLocationSearched?: (location: MapLocation) => void;
  clickableMarker?: boolean;
};

const MAP_OVERLAY_HINT_DESKTOP = "Click the map to zoom and pan";
const MAP_OVERLAY_HINT_TOUCH =
  "Tap the map to move it · use two fingers to zoom";

const PickupMap = ({
  locations,
  onLocationSearched,
  clickableMarker = false,
}: PickupMapProps) => {
  const [foundLocation, setFoundLocation] = useState<MapLocation | null>();
  const [placedByClick, setPlacedByClick] = useState(false);
  const mapRef = useRef(null);
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  const lockUntilClick = !clickableMarker;
  const [mapInteractive, setMapInteractive] = useState(!lockUntilClick);
  const [isCoarsePointer, setIsCoarsePointer] = useState(false);

  useEffect(() => {
    setIsCoarsePointer(window.matchMedia("(pointer: coarse)").matches);
  }, []);

  const deactivateMap = useCallback(() => setMapInteractive(false), []);

  useMapInteractionLock(
    mapWrapperRef,
    mapInteractive,
    lockUntilClick,
    deactivateMap,
  );

  const handleOverlayActivate = () => setMapInteractive(true);

  const handleMapClick = async (lat: number, lng: number) => {
    const fallback: MapLocation = {
      address: `Selected (${lat.toFixed(5)}, ${lng.toFixed(5)})`,
      latitude: lat,
      longitude: lng,
    };

    setPlacedByClick(true);
    onLocationSearched?.(fallback);
    setFoundLocation(fallback);

    try {
      const address = await reverseGeocode(lat, lng);
      const loc: MapLocation = { address, latitude: lat, longitude: lng };
      onLocationSearched?.(loc);
      setFoundLocation(loc);
    } catch {
      // TODO:keep fallback address
    }
  };

  const handleLocationFound = (loc: MapLocation) => {
    setPlacedByClick(false);
    onLocationSearched?.(loc);
    setFoundLocation(loc);
  };

  const markers = locations.map((location) => {
    const { address, longitude, latitude } = location;
    const coords = [latitude, longitude];

    return (
      <Marker key={address} position={coords as L.LatLngExpression}>
        <Popup>
          <div>{address}</div>
        </Popup>
      </Marker>
    );
  });

  let newLocationMarker = null;

  if (foundLocation) {
    const { address, longitude, latitude } = foundLocation; //What here?
    const coords = [latitude, longitude];

    newLocationMarker = (
      <Marker key={address} position={coords as L.LatLngExpression}>
        <Popup>
          <div>{address}</div>
        </Popup>
      </Marker>
    );
  }

  const overlayHint = isCoarsePointer
    ? MAP_OVERLAY_HINT_TOUCH
    : MAP_OVERLAY_HINT_DESKTOP;

  return (
    <div
      ref={mapWrapperRef}
      className={`map-wrapper${mapInteractive ? " map-wrapper--active" : ""}`}
    >
      <MapContainer
        center={MAP_CENTER}
        zoom={MAP_ZOOM}
        className={`map ${clickableMarker ? "map-clickable-marker" : ""}`}
        ref={mapRef}
        scrollWheelZoom={false}
        dragging={!lockUntilClick}
        touchZoom={!lockUntilClick}
        doubleClickZoom={!lockUntilClick}
      >
        {lockUntilClick && (
          <MapInteractionController interactive={mapInteractive} />
        )}
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
        />
        <MapCenterHandler
          locations={locations}
          foundLocation={foundLocation}
          recenterToFoundLocation={!placedByClick}
        />

        <div>{newLocationMarker ? newLocationMarker : markers}</div>

        {onLocationSearched && (
          <>
            {clickableMarker && <MapClickHandler onClick={handleMapClick} />}
            <SearchControl onLocationFound={handleLocationFound} />
          </>
        )}
      </MapContainer>
      {lockUntilClick && !mapInteractive && (
        <button
          type="button"
          className="map-interaction-overlay"
          aria-label={overlayHint}
          onClick={handleOverlayActivate}
        >
          <span className="map-interaction-overlay__text">{overlayHint}</span>
        </button>
      )}
    </div>
  );
};

export default PickupMap;
